import { useSiteData } from "@/context/SiteDataContext";
import { waLink } from "@/lib/api";
import { WhatsAppIcon } from "./WhatsAppIcon";

const InstagramIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);
const FacebookIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M13.5 21v-7.5h2.5l.4-3h-2.9V8.6c0-.9.3-1.5 1.5-1.5h1.5V4.4c-.3 0-1.2-.1-2.2-.1-2.2 0-3.7 1.3-3.7 3.8v2.4H8v3h2.6V21h2.9z" />
  </svg>
);
const YouTubeIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M21.6 7.2a2.5 2.5 0 0 0-1.8-1.8C18.2 5 12 5 12 5s-6.2 0-7.8.4A2.5 2.5 0 0 0 2.4 7.2 26 26 0 0 0 2 12a26 26 0 0 0 .4 4.8 2.5 2.5 0 0 0 1.8 1.8C5.8 19 12 19 12 19s6.2 0 7.8-.4a2.5 2.5 0 0 0 1.8-1.8A26 26 0 0 0 22 12a26 26 0 0 0-.4-4.8zM10 15V9l5.2 3L10 15z" />
  </svg>
);

const NETWORKS = [
  { key: "instagram_url", id: "instagram", label: "Instagram", icon: InstagramIcon, color: "bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF]" },
  { key: "facebook_url", id: "facebook", label: "Facebook", icon: FacebookIcon, color: "bg-[#1877F2]" },
  { key: "youtube_url", id: "youtube", label: "YouTube", icon: YouTubeIcon, color: "bg-[#FF0000]" },
];

export const useSocials = () => {
  const { settings } = useSiteData();
  return NETWORKS.filter((n) => settings[n.key]?.trim()).map((n) => ({ ...n, href: settings[n.key].trim() }));
};

export const SocialIcons = ({ prefix, size = "h-10 w-10" }) => {
  const { settings } = useSiteData();
  const socials = useSocials();
  return (
    <div className="flex flex-wrap gap-3" data-testid={`${prefix}-social-icons`}>
      {socials.map(({ id, label, icon: Icon, color, href }) => (
        <a key={id} href={href} target="_blank" rel="noopener noreferrer" aria-label={`Ocean Wings Travel on ${label}`} title={label} data-testid={`${prefix}-social-${id}`}
          className={`${size} ${color} rounded-full grid place-items-center text-white shadow-md hover:scale-110 transition-transform duration-200`}><Icon className="h-5 w-5" /></a>
      ))}
      <a href={waLink(settings.whatsapp, "Hi Ocean Wings Travel, I'd like to plan an Andaman trip.")} target="_blank" rel="noopener noreferrer" aria-label="Ocean Wings Travel on WhatsApp" title="WhatsApp" data-testid={`${prefix}-social-whatsapp`}
        className={`${size} bg-emerald-500 rounded-full grid place-items-center text-white shadow-md hover:scale-110 transition-transform duration-200`}><WhatsAppIcon className="h-5 w-5" /></a>
    </div>
  );
};

export const HomeSocial = () => {
  const { settings } = useSiteData();
  const socials = useSocials();
  return (
    <section className="container-x pt-16" data-testid="home-social">
      <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-10 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
        <div className="max-w-xl">
          <p className="eyebrow">Follow Ocean Wings Travel</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl text-navy font-semibold">Island moments, trip updates and travel tips</h2>
          <p className="mt-2 text-slate-600">Follow us on social media, or message us on WhatsApp for a quick enquiry.</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:min-w-[440px]">
          {socials.map(({ id, label, icon: Icon, color, href }) => (
            <a key={id} href={href} target="_blank" rel="noopener noreferrer" data-testid={`home-social-${id}`}
              className="flex items-center gap-3 rounded-2xl border border-slate-200 p-3 pr-5 hover:border-ocean hover:shadow-md transition-[border-color,box-shadow]">
              <span className={`h-11 w-11 rounded-xl ${color} grid place-items-center text-white`}><Icon className="h-5 w-5" /></span>
              <span><span className="block font-semibold text-navy">{label}</span><span className="block text-xs text-slate-500">Follow Ocean Wings Travel</span></span>
            </a>
          ))}
          <a href={waLink(settings.whatsapp, "Hi Ocean Wings Travel, I'd like to plan an Andaman trip.")} target="_blank" rel="noopener noreferrer" data-testid="home-social-whatsapp"
            className="flex items-center gap-3 rounded-2xl bg-emerald-500 p-3 pr-5 text-white hover:bg-emerald-600 transition-colors">
            <span className="h-11 w-11 rounded-xl bg-white/15 grid place-items-center"><WhatsAppIcon className="h-5 w-5" /></span>
            <span><span className="block font-semibold">WhatsApp enquiry</span><span className="block text-xs text-emerald-50">{settings.whatsapp}</span></span>
          </a>
        </div>
      </div>
    </section>
  );
};
